/*
  Theme-owned Klaviyo back-in-stock signup.

  Posts to Klaviyo's documented client-side endpoint, which is what Klaviyo
  point custom Shopify themes at (they only auto-install BIS on certain free
  themes). Public company id only. Never put a private key in a theme asset.

  Replaces the legacy klaviyo.init / klaviyo.enable("backinstock") bootstrap,
  which stopped working once the Klaviyo app embed began claiming
  window.klaviyo as the modern object SDK before the legacy array contract
  could be created. Both calls were rejected as unknown events.

  Framework-agnostic on purpose. Two buy areas render this snippet and they
  use different variant switchers:
    main PDP    Alpine,   button.add-to-cart-btn
    merch page  plain JS, button[data-add-to-cart]
  Rather than bind to either, this watches the add-to-cart button's `disabled`
  attribute (a reflected boolean both switchers set) and reads the variant id
  from the form's [name="id"] input at submit time. Reading the id late avoids
  needing to observe a hidden input whose value is set without firing events.

  Everything downstream is unchanged and already working: the "Subscribed to
  Back in Stock" metric, the WC | Back In Stock Flow, and the minimum-inventory
  settings in Klaviyo.
*/
(function () {
  'use strict';

  if (window.__williesBisInit) return;
  window.__williesBisInit = true;

  var COMPANY_ID = 'UmGxEm';
  var REVISION = '2026-07-15';
  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';

  /* Klaviyo's catalog variant id format for the Shopify integration. */
  var VARIANT_PREFIX = '$shopify:::$default:::';

  var ATC_SELECTOR = '[data-add-to-cart], .add-to-cart-btn, .merch-product__add-to-cart';
  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  function setMessage(root, text, kind) {
    var msg = root.querySelector('[data-bis-msg]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.hidden = !text;
    msg.className = 'willies-bis__msg' + (kind ? ' willies-bis__msg--' + kind : '');
  }

  function subscribe(root, scope) {
    var emailEl = root.querySelector('[data-bis-email]');
    var submitEl = root.querySelector('[data-bis-submit]');
    var email = emailEl ? (emailEl.value || '').trim() : '';

    if (!EMAIL_RE.test(email)) {
      setMessage(root, 'Please enter a valid email address.', 'error');
      return;
    }

    var idInput = scope ? scope.querySelector('[name="id"]') : null;
    var variantId = idInput && idInput.value;

    if (!variantId) {
      setMessage(root, 'Sorry, something went wrong. Please try again.', 'error');
      return;
    }

    if (submitEl) submitEl.disabled = true;
    if (emailEl) emailEl.disabled = true;
    setMessage(root, '', null);

    fetch(ENDPOINT + '?company_id=' + encodeURIComponent(COMPANY_ID), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: REVISION
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: VARIANT_PREFIX + variantId
              }
            }
          }
        }
      })
    })
      .then(function (res) {
        /* Klaviyo returns 202 Accepted on success. */
        if (!res.ok && res.status !== 202) throw new Error('HTTP ' + res.status);
        setMessage(root, "You're in! We'll let you know when it's back.", 'success');
        if (emailEl) emailEl.value = '';
      })
      .catch(function () {
        setMessage(root, 'Sorry, that did not go through. Please try again.', 'error');
        if (submitEl) submitEl.disabled = false;
        if (emailEl) emailEl.disabled = false;
      });
  }

  function setup(root) {
    if (root.__bisBound) return;
    root.__bisBound = true;

    /* The form is the reliable scope: it holds both the add-to-cart button and
       the [name="id"] input on either buy area. Fall back to the document for
       any future caller that renders this outside a form. */
    var scope = root.closest('form') || document;
    var atc = scope.querySelector(ATC_SELECTOR) || document.querySelector(ATC_SELECTOR);

    function sync() {
      /* No add-to-cart button at all means we cannot tell stock state, so stay
         hidden rather than risk showing beside a live buy button. */
      root.hidden = !atc || !atc.hasAttribute('disabled');
    }

    sync();

    if (atc) {
      new MutationObserver(sync).observe(atc, {
        attributes: true,
        attributeFilter: ['disabled']
      });
    }

    /* Opening the panel is handled by the delegated capture listener below,
       not here. Binding it on the element as well would run it twice and
       would not stop the event reaching product.js. */

    root.__bisScope = scope;
  }

  /* Our markup sits INSIDE the product form, and product.js binds a handler on
     the form in the CAPTURE phase. Capture runs ancestor to target, so it fires
     BEFORE anything we could bind on our own elements, and stopPropagation from
     a bubble-phase handler cannot reach it. It JSON.parses a payload our events
     do not carry and throws `SyntaxError: "undefined" is not valid JSON`
     (product.js:351) on sold-out merch PDPs.

     Document-level capture fires before the form's capture listener, so we
     handle our events here and stop them there. Bound ONCE behind the
     window.__williesBisInit flag above, because a section's script can be
     emitted per instance on this theme. */
  function delegate(type, handler) {
    document.addEventListener(type, function (e) {
      var root = e.target && e.target.closest && e.target.closest('[data-willies-bis]');
      if (!root) return;
      handler(e, root);
    }, true);
  }

  function init() {
    var roots = document.querySelectorAll('[data-willies-bis]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  delegate('click', function (e, root) {
    var trigger = e.target.closest('[data-bis-trigger]');
    var submit = e.target.closest('[data-bis-submit]');
    if (!trigger && !submit) return;

    e.preventDefault();
    e.stopPropagation();

    if (trigger) {
      trigger.hidden = true;
      var panel = root.querySelector('[data-bis-panel]');
      if (panel) panel.hidden = false;
      var email = root.querySelector('[data-bis-email]');
      if (email) email.focus();
      return;
    }

    subscribe(root, root.__bisScope || root.closest('form') || document);
  });

  delegate('keydown', function (e, root) {
    if (e.key !== 'Enter') return;
    if (!e.target.closest('[data-bis-email]')) return;

    /* Enter inside the product form would otherwise submit it and add to cart. */
    e.preventDefault();
    e.stopPropagation();
    subscribe(root, root.__bisScope || root.closest('form') || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
